import type { DeepPartial, Look, ScenePreset } from './types';

export const FONT_OPTIONS: { label: string; value: string }[] = [
  { label: '宋体 / 思源宋体', value: '"Noto Serif SC","Source Han Serif SC","Noto Serif CJK SC","Songti SC","STSong","SimSun",serif' },
  { label: '楷体', value: '"Kaiti SC","STKaiti","KaiTi","BiauKai","AR PL UKai CN","LXGW WenKai",serif' },
  { label: '文楷（需本机安装）', value: '"LXGW WenKai","霞鹜文楷","Kaiti SC","STKaiti","KaiTi",serif' },
  { label: '行楷（需本机安装）', value: '"STXingkai","Xingkai SC","华文行楷","Kaiti SC","KaiTi",serif' },
  { label: '隶书（需本机安装）', value: '"STLiti","LiSu","隶书","Baoli SC","Kaiti SC",serif' },
  { label: '黑体 / 思源黑体', value: '"Noto Sans SC","Source Han Sans SC","PingFang SC","Microsoft YaHei","Heiti SC",sans-serif' },
];

/** 西方金殿：深青金石夜色中的金色佛光——默认场景 */
export const SCENE_JINDIAN: Look = {
  name: '西方金殿',
  background: { top: '#05061a', bottom: '#1d0f26', nebula: 0.55, nebulaScale: 1.6, vignette: 0.75 },
  colors: { primary: '#ffc45e', secondary: '#ff6f3c', accent: '#79c8ff' },
  halo: { x: 0.5, y: 0.42, radius: 0.2, intensity: 1.0, bloom: 0.55, mandorla: 0.25 },
  rays: { count: 18, intensity: 0.22, sharpness: 3.5, speed: 0.01, length: 0.9, shimmer: 0.5 },
  rings: { count: 4, intensity: 0.25, spacing: 0.085, width: 0.006, speed: 0.012, petals: 8, petalDepth: 0.07, pulse: 0.6 },
  particles: { mode: 'motes', count: 160, size: 1.0, speed: 0.6, intensity: 0.8, spread: 0.9 },
  text: {
    font: FONT_OPTIONS[0].value,
    weight: 600,
    size: 6,
    spacing: 0.3,
    layout: 'right',
    margin: 4,
    color: '#fff3d6',
    glowColor: '#ffb648',
    glow: 0.8,
    animation: 'bloom',
    duration: 1.1,
  },
  reactive: {
    mode: 'mix',
    mix: 0.45,
    sensitivity: 1.2,
    attack: 40,
    release: 380,
    threshold: 0.08,
    agc: true,
    bandLow: 0.8,
    bandLowMid: 1.0,
    bandHighMid: 0.7,
    bandHigh: 0.35,
    toGlow: 0.8,
    toRays: 0.7,
    toRings: 0.8,
    toParticles: 0.6,
    toText: 0.6,
    lyricPulse: 0.55,
  },
  render: { scale: 0.75, exposure: 1.0 },
};

/** 莲池月色：清冷月光、莲瓣天花 */
export const SCENE_LIANCHI: Look = merge(SCENE_JINDIAN, {
  name: '莲池月色',
  background: { top: '#030a18', bottom: '#082a36', nebula: 0.4, nebulaScale: 2.2, vignette: 0.7 },
  colors: { primary: '#ffe9f1', secondary: '#ff86b0', accent: '#8ff0d6' },
  halo: { y: 0.38, radius: 0.16, intensity: 0.85, bloom: 0.7, mandorla: 0.05 },
  rays: { count: 16, intensity: 0.18, sharpness: 4, speed: -0.008, length: 0.75, shimmer: 0.7 },
  rings: { count: 4, intensity: 0.42, spacing: 0.1, width: 0.005, speed: 0.008, petals: 12, petalDepth: 0.05, pulse: 0.5 },
  particles: { mode: 'petals', count: 70, size: 1.2, speed: 0.4, intensity: 0.75, spread: 1 },
  text: {
    font: FONT_OPTIONS[1].value,
    weight: 500,
    size: 6.5,
    spacing: 0.4,
    color: '#fbf6ff',
    glowColor: '#ff9cc4',
    glow: 0.7,
    animation: 'ink',
    duration: 1.4,
  },
  reactive: { mode: 'loudness', release: 520, toRays: 0.5, toParticles: 0.4 },
});

/** 琉璃宝光：紫青琉璃、放射光粒 */
export const SCENE_LIULI: Look = merge(SCENE_JINDIAN, {
  name: '琉璃宝光',
  background: { top: '#030212', bottom: '#150a36', nebula: 0.7, nebulaScale: 1.2, vignette: 0.8 },
  colors: { primary: '#c3b2ff', secondary: '#3fdcff', accent: '#ffd27a' },
  halo: { y: 0.44, radius: 0.22, intensity: 0.95, bloom: 0.5, mandorla: 0.35 },
  rays: { count: 24, intensity: 0.26, sharpness: 4, speed: 0.012, length: 1.0, shimmer: 0.35 },
  rings: { count: 7, intensity: 0.55, spacing: 0.065, width: 0.004, speed: 0.02, petals: 16, petalDepth: 0.04, pulse: 0.8 },
  particles: { mode: 'radiate', count: 220, size: 0.8, speed: 0.9, intensity: 0.9, spread: 0.8 },
  text: {
    font: FONT_OPTIONS[0].value,
    weight: 700,
    size: 6,
    spacing: 0.45,
    color: '#f4f0ff',
    glowColor: '#7fe7ff',
    glow: 0.9,
    animation: 'stagger',
    duration: 0.9,
  },
  reactive: { mode: 'bands', bandLow: 1.0, bandLowMid: 0.8, bandHighMid: 0.9, bandHigh: 0.6 },
});

export const BUILTIN_SCENES: ScenePreset[] = [
  { id: 'jindian', name: SCENE_JINDIAN.name, look: SCENE_JINDIAN, builtin: true },
  { id: 'lianchi', name: SCENE_LIANCHI.name, look: SCENE_LIANCHI, builtin: true },
  { id: 'liuli', name: SCENE_LIULI.name, look: SCENE_LIULI, builtin: true },
];

/* ------------------------------------------------------------------ */
/* 工具                                                                */
/* ------------------------------------------------------------------ */

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** 深合并：只合并 base 中已存在的键，类型不符的值会被忽略，保证导入的预设不会破坏结构。 */
export function merge<T>(base: T, patch: DeepPartial<T> | undefined | null): T {
  const out = structuredClone(base) as Record<string, unknown>;
  if (!isPlainObject(patch)) return out as T;
  for (const [k, v] of Object.entries(patch)) {
    if (!(k in out)) continue;
    const cur = out[k];
    if (isPlainObject(cur)) {
      out[k] = merge(cur, v as DeepPartial<typeof cur>);
    } else if (v !== undefined && typeof v === typeof cur) {
      out[k] = v;
    }
  }
  return out as T;
}

export function getPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((o, k) => (isPlainObject(o) ? o[k] : undefined), obj);
}

export function setPath(obj: unknown, path: string, value: unknown): void {
  const keys = path.split('.');
  let o = obj as Record<string, unknown>;
  for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]] as Record<string, unknown>;
  o[keys[keys.length - 1]] = value;
}

/* ------------------------------------------------------------------ */
/* 用户预设（localStorage）                                             */
/* ------------------------------------------------------------------ */

const PRESET_KEY = 'nianfo.presets.v1';

export interface PresetFile {
  format: 'nianfo-presets';
  version: 1;
  presets: { id?: string; name: string; look: DeepPartial<Look> }[];
}

export function loadUserPresets(): ScenePreset[] {
  try {
    const raw = localStorage.getItem(PRESET_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw) as ScenePreset[];
    return data.map((p) => ({ id: p.id, name: p.name, look: merge(SCENE_JINDIAN, p.look) }));
  } catch {
    return [];
  }
}

export function saveUserPresets(presets: ScenePreset[]): void {
  localStorage.setItem(PRESET_KEY, JSON.stringify(presets.filter((p) => !p.builtin)));
}

export function exportPresets(presets: ScenePreset[]): string {
  const file: PresetFile = {
    format: 'nianfo-presets',
    version: 1,
    presets: presets.map((p) => ({ id: p.id, name: p.name, look: p.look })),
  };
  return JSON.stringify(file, null, 2);
}

/** 接受 PresetFile、单个 {name, look}，或单纯的 Look 对象 */
export function importPresets(text: string): ScenePreset[] {
  const data = JSON.parse(text) as unknown;
  const list: { id?: string; name?: string; look?: unknown }[] = [];
  if (isPlainObject(data) && Array.isArray(data.presets)) list.push(...(data.presets as typeof list));
  else if (isPlainObject(data) && isPlainObject(data.look)) list.push(data as (typeof list)[number]);
  else if (isPlainObject(data) && isPlainObject(data.halo)) list.push({ name: String(data.name ?? '导入场景'), look: data });
  else throw new Error('文件中没有可识别的场景预设');
  return list.map((p) => {
    const look = merge(SCENE_JINDIAN, p.look as DeepPartial<Look>);
    const name = p.name || look.name || '导入场景';
    look.name = name;
    return { id: 'user-' + uid(), name, look };
  });
}

export function uid(): string {
  return Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4);
}
